/**
 * SelectFirstTip — one-time coach for the select→drill-in model (Phase 5 / E3).
 *
 * The select-first grid (grid 0.8.40) changed muscle memory: a first click now
 * SELECTS a block instead of dropping a caret. New users need one line teaching
 * the three-verb model — select / edit / move — after which it's obvious. Shown
 * once per browser (localStorage), dismissable, edit-mode only. Deliberately
 * tiny: a bottom-center pill, never a modal. Says "tap" on a coarse pointer.
 *
 * Sits right above the selection bar, at a `bottom` derived from the bar's
 * measured height (the bar wraps to two rows on a phone).
 */
import { useState } from "react";
import { css, html } from "react-strict-dom";

import { stackBottom, useBarHeight } from "./bottomStack.js";
import { coachPill } from "./coachPill.js";
import { usePointerCoarse } from "./usePointerCoarse.js";

const SEEN_KEY = "wb-selectfirst-tip-v1";

const s = css.create({
  at: (bottom: number) => ({ bottom }),
});

function alreadySeen(): boolean {
  try {
    return (
      typeof localStorage !== "undefined" &&
      localStorage.getItem(SEEN_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export function SelectFirstTip(): React.ReactElement | null {
  const [seen, setSeen] = useState(alreadySeen);
  const coarse = usePointerCoarse();
  const barHeight = useBarHeight();
  if (seen) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(SEEN_KEY, "1");
    } catch {
      // private-mode / storage-disabled: still dismiss for this session.
    }
    setSeen(true);
  };
  const press = coarse ? "Tap" : "Click";
  const again = coarse ? "tap again" : "click again";

  return (
    <html.div
      role="status"
      aria-live="polite"
      // Right above the selection bar, which owns the bottom slot.
      style={[coachPill.pill, s.at(stackBottom(barHeight, 0))]}
    >
      <html.span>
        💡 <html.strong>{press}</html.strong> a block to select ·{" "}
        <html.strong>{again}</html.strong> to edit ·{" "}
        <html.strong>drag</html.strong> to move
      </html.span>
      <html.button type="button" onClick={dismiss} style={coachPill.dismiss}>
        Got it
      </html.button>
    </html.div>
  );
}
